import React, { type ComponentProps, forwardRef, useCallback } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';

import {
  pointerEventsStyles,
  BagIcon,
  Button,
  useNavbarContainerHeight,
  useResponsive,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuFloatingButtonsBagButton = forwardRef<View, MenuFloatingButtonsBagButtonProps>(
  (props, ref) => {
    const { lineItemsCount, disabled, style, ...restProps } = props;

    const { currentBreakpoint } = useResponsive();
    const { offsetStyle: navbarOffsetStyle } = useNavbarContainerHeight();

    // ─── Flags ───────────────────────────────────────────────────────────

    const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');
    const shouldUseCompactLayout = isNavbarV2Enabled && currentBreakpoint.isXS;

    // ─────────────────────────────────────────────────────────────────────

    const BagIconWithCount = useCallback(
      () => <BagIcon bagCount={lineItemsCount} />,
      [lineItemsCount],
    );

    // ─── Styles ──────────────────────────────────────────────────────────

    const containerStyles = [
      styles.container,
      shouldUseCompactLayout ? styles.containerCompact : undefined,
      shouldUseCompactLayout ? navbarOffsetStyle : undefined,
    ];

    // ─────────────────────────────────────────────────────────────────────

    return (
      <View style={[containerStyles, pointerEventsStyles['box-none']]}>
        <Button
          ref={ref}
          size="large"
          palette="lime"
          style={[styles.button, style]}
          disabled={disabled}
          rightElement={BagIconWithCount}
          {...restProps}
        >
          <FormattedMessage {...messages.label} />
        </Button>
      </View>
    );
  },
);

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  label: {
    defaultMessage: 'View bag',
    description: 'Menu | Floating Button | Bag | Label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    alignItems: 'flex-end',
    paddingVertical: theme.spacing['6'],
    paddingHorizontal: theme.spacing['4'],
  },
  containerCompact: {
    paddingVertical: theme.spacing['1'],
  },
  button: {
    ...theme.elevations['12'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type MenuFloatingButtonsBagButtonProps = {
  lineItemsCount: number;
} & Omit<ComponentProps<typeof Button>, 'ref' | 'children' | 'rightElement'>;
